import {
    useEffect,
    useState,
} from 'react';
import {I18n} from 'aws-amplify/utils';
import {Button} from '@mantine/core';

import {DnsZone} from '../types';
import {DelegateZoneDrawer} from '../drawers/DelegateZoneDrawer';
import {DEFAULT_DELEGATION_TTL_SECONDS} from '../services/delegation';

// FNP-1466 — the delegation control, on the ZONE DETAIL PAGE toolbar.
//
// ⚠️ THE DETAIL PAGE, NOT THE LIST, AND THE REASON IS THE DATA. The list does
// not carry nameservers — the Route 53 adapter says so at `listZones`:
// "ListHostedZones does not return the delegation set, and fetching it per zone
// would turn one call into N. Zones list with no nameservers; getZone is where
// they arrive." A delegation record IS the child's nameservers, so a control on
// the list has nothing to build from and has to re-fetch the zone it is already
// looking at.
//
// The detail page has already fetched the whole zone. The values are on screen
// under Name Servers, the delegation status is in the Profile beside them, and
// the operator is looking at the one zone the operation concerns. Everything
// this needs is already here.
//
// ⚠️ "MANAGE", NOT "DELEGATE", because it does both. A zone already delegated
// needs its delegation REMOVED before the zone is deleted, or the parent keeps
// an NS record pointing at nameservers that no longer answer — a lame
// delegation, which times out rather than failing cleanly and which silently
// hands traffic to whoever next creates that name. Hiding removal behind a
// different control would leave the hazard one step further away than the thing
// that causes it.

export const ManageDelegationButton = (
    {
        zone,
    }: {
        zone: DnsZone,
    },
) => {

    const [opened, setOpened] = useState(false);

    // ⚠️ THE DRAWER'S FORM STATE LIVES HERE, NOT IN THE DRAWER, BECAUSE THE
    // DRAWER REMOUNTS AND THIS DOES NOT.
    //
    // The detail page rebuilds its `tools` array whenever `useZone` settles,
    // which remounts everything inside it — and a remount discards `useState`
    // AND gives the component a fresh `useRef`, so neither survives there. The
    // symptom was silent and nasty: the connection picker emptied between the
    // operator choosing a connection and pressing the button, so the click did
    // nothing, no request was made, and nothing on screen said why.
    const [ttlSeconds, setTtlSeconds] = useState(DEFAULT_DELEGATION_TTL_SECONDS);
    const [parentConnectionId, setParentConnectionId] = useState<string | null>(null);

    // Opening the drawer on a DIFFERENT zone must not inherit a connection
    // chosen for another parent — that would write a delegation through a
    // credential the operator picked for something else.
    useEffect(() => {
        setParentConnectionId(null);
        setTtlSeconds(DEFAULT_DELEGATION_TTL_SECONDS);
    }, [zone.id]);

    return (
        <>
            <Button
                variant={'outline'}
                onClick={() => setOpened(true)}
                data-testid={'manage-delegation-button'}
            >
                {I18n.get('Manage Delegation')}
            </Button>
            <DelegateZoneDrawer
                zone={zone}
                form={{
                    ttlSeconds,
                    setTtlSeconds,
                    parentConnectionId,
                    setParentConnectionId,
                }}
                opened={opened}
                onClose={() => setOpened(false)}
            />
        </>
    );
};
